import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";

export default function Hero({ stockCount }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="on-dark relative h-[100svh] min-h-[640px] overflow-hidden bg-forest text-paper">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src="/img/hero.webp" alt="Dark green Defender parked on a gravel courtyard beside a Cotswold stone barn at first light"
          className="h-full w-full object-cover object-[70%_center]" fetchPriority="high"
          initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 2.2, ease: [0.2, 0.7, 0.2, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(15,38,32,.82)_0%,rgba(15,38,32,.45)_45%,rgba(15,38,32,0)_75%)]" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0F2620]/80 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-5 pb-16 sm:px-8 lg:px-12 lg:pb-20">
        <motion.p className="eyebrow text-stone" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}>
          Cirencester <span className="mx-2">•</span> The Cotswolds
        </motion.p>
        <MaskLines
          lines={["Made for life", <em key="e" className="font-normal italic text-stone">out here.</em>]}
          className="mt-6 font-serif text-[3.5rem] leading-[0.92] sm:text-[5.5rem] lg:text-[8rem]"
          delay={0.35}
        />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 1 }}
          className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <p className="max-w-md text-base leading-relaxed text-paper/90 sm:text-lg">
            Specialist 4x4 &amp; SUV sales in Cirencester, at the heart of the Cotswolds. Carefully selected. Properly prepared.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/stock" className="btn btn-light" data-testid="hero-view-stock">View Stock <ArrowRight className="arrow h-4 w-4" /></Link>
            <Link to="/sell-your-4x4" className="btn btn-ghost-light" data-testid="hero-sell">Sell Your 4x4</Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
          className="mt-12 hidden items-center justify-between border-t border-paper/25 pt-5 text-xs uppercase tracking-[0.2em] text-stone sm:flex">
          <span data-testid="hero-stock-count">{stockCount ? `${stockCount} vehicles currently in stock` : "Current stock"}</span>
          <span className="flex items-center gap-2">Scroll <ArrowDown className="h-3.5 w-3.5 animate-bounce" /></span>
        </motion.div>
      </motion.div>
    </section>
  );
}
